import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { formatDateTime } from '@/utils/date'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

// AI-ALL-ENV-CARD-1 — "AI spend — all environments" on Admin Monitor. Loads by
// itself from /v1/admin/ai-summary/all (this site + peers, up to ~8 s). Shows
// only name / status / calls / cost / as-of per site and the combined total;
// db_id, source and every by_* key in the reply are deliberately never read.

interface SiteRow {
  name: string
  status: string
  reason: string | null
  generated_at: string | null
  calls: number | null
  cost_usd: number | null
}

interface AllEnvSummary {
  sites?: SiteRow[]
  total?: { calls?: number | null; cost_usd?: number | null }
}

type RangeKey = 'today' | '7d' | '30d' | 'all'

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All time' },
]

const STATUS_WORDS: Record<string, string> = {
  ok: 'Counted',
  unreachable: 'Unreachable',
  refused: 'Refused',
  not_configured: 'Not configured',
  invalid: 'Invalid reply',
}

function fmtUsd(n: number | null | undefined) {
  if (n === null || n === undefined) return '—'
  return `$${n.toFixed(4)}`
}

function fmtCalls(n: number | null | undefined) {
  return n === null || n === undefined ? '—' : String(n)
}

function statusText(site: SiteRow) {
  const reason = (site.reason ?? '').trim()
  if (site.status === 'ok') {
    if (site.calls !== null) return STATUS_WORDS.ok
    // Same-database twin: ok, but its numbers are counted on another row.
    return reason ? reason.charAt(0).toUpperCase() + reason.slice(1) : 'Not counted'
  }
  const word = STATUS_WORDS[site.status] ?? site.status
  if (!reason || reason.toLowerCase() === word.toLowerCase()) return word
  return `${word} — ${reason}`
}

export function AiAllEnvSection() {
  const { data, loading, error, execute } = useApi<AllEnvSummary>()
  const [range, setRange] = useState<RangeKey>('today')

  const load = useCallback(
    (r: RangeKey) => {
      execute(() =>
        apiClient
          .get<AllEnvSummary>('/v1/admin/ai-summary/all', { params: { range: r } })
          .then(res => res.data),
      )
    },
    [execute],
  )

  useEffect(() => {
    load(range)
  }, [load, range])

  const sites = data?.sites ?? []
  const total = data?.total

  return (
    <section data-testid="ai-all-env-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">AI spend — all environments</h2>
        <div className="flex gap-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              type="button"
              data-testid={`ai-all-env-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-2 py-1 text-xs rounded ${
                range === r.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div data-testid="ai-all-env-error" className="text-sm text-red-600">
          {error}
        </div>
      )}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {data && !loading && (
        <>
          {total && (
            <div
              data-testid="ai-all-env-total"
              className="flex flex-wrap gap-3 rounded-lg border bg-gray-50 px-4 py-3 text-sm mb-3"
            >
              <span>
                Combined: {fmtCalls(total.calls)} calls · {fmtUsd(total.cost_usd)}
              </span>
            </div>
          )}

          {sites.length === 1 && (
            <p data-testid="ai-all-env-solo" className="text-sm text-gray-500 mb-3">
              Other environments are not connected yet.
            </p>
          )}

          {sites.length > 0 && (
            <div className="border rounded-lg overflow-x-auto mb-3">
              <Table data-testid="ai-all-env-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Site</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Calls</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>As of</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sites.map((s, i) => (
                    <TableRow
                      key={`${s.name}-${i}`}
                      data-testid={`ai-all-env-row-${s.name}`}
                      className={s.calls === null ? 'text-gray-400' : undefined}
                    >
                      <TableCell className="text-sm">{s.name}</TableCell>
                      <TableCell className="text-sm" data-testid={`ai-all-env-status-${s.name}`}>
                        {statusText(s)}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`ai-all-env-calls-${s.name}`}>
                        {fmtCalls(s.calls)}
                      </TableCell>
                      <TableCell
                        className="text-sm font-mono"
                        data-testid={`ai-all-env-cost-${s.name}`}
                      >
                        {fmtUsd(s.cost_usd)}
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(s.generated_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
